/**
 * Hub's binding of the shared user-facing error mapping.
 *
 * The SQLSTATE→message map itself lives in `@wtc/api-errors` — the canonical,
 * cross-app home it was promoted to. This file exists only to pre-bind the two
 * refusals that are Hub-domain (and therefore can't live in a package that must
 * not know Hub's types), and to keep `@/lib/utils/api/user-facing-errors` as the
 * import path ~40 Hub call sites already use.
 *
 * NEVER return raw `error.message` from the database to clients — log the raw
 * error with `logger.error(message, error, context)` and return these messages.
 */

import {
  mapToUserFacingError as mapToUserFacingErrorShared,
  type MapToUserFacingErrorContext,
  type UserFacingErrorResult,
  type UserFacingPreCheck,
} from "@wtc/api-errors";

import {
  contactMethodConflictMessage,
  parseContactMethodConflict,
} from "@/lib/utils/contact/method-keys";
import {
  parseQuickBooksCompanyCustomerConflict,
  parseQuickBooksCustomerConflict,
  quickBooksCompanyCustomerConflictMessage,
  quickBooksCustomerConflictMessage,
} from "@/lib/quickbooks/customer-links/conflict";

export type {
  PostgresLikeError,
  UserFacingErrorBody,
  UserFacingErrorResult,
  MapToUserFacingErrorContext,
  UserFacingPreCheck,
} from "@wtc/api-errors";

/**
 * One-method-one-owner refusal. Runs BEFORE the generic 23505 branch, which
 * would flatten it to "A record with these details already exists." — useless
 * here, where the user needs to know WHO holds the number and which of the two
 * fixes applies (put a shared line on the company, or merge).
 */
const contactMethodPreCheck: UserFacingPreCheck = (error) => {
  const methodConflict = parseContactMethodConflict(error);
  if (!methodConflict) return null;
  return {
    status: 409,
    code: "CONTACT_METHOD_TAKEN",
    error: contactMethodConflictMessage(methodConflict),
  };
};

/**
 * One-customer-one-company refusal. Also ahead of 23505: the generic
 * "A record with these details already exists." hides WHICH company holds the
 * QuickBooks customer, which is the only actionable part.
 */
const quickBooksCustomerPreCheck: UserFacingPreCheck = (error) => {
  const quickBooksConflict = parseQuickBooksCustomerConflict(error);
  if (!quickBooksConflict) return null;
  return {
    status: 409,
    code: "QUICKBOOKS_CUSTOMER_TAKEN",
    error: quickBooksCustomerConflictMessage(quickBooksConflict),
  };
};

/**
 * The REVERSE refusal: this company already bills from a customer in that
 * QuickBooks company file. A separate pre-check from the one above because
 * `cqc_assert_single_owner()` raises it under its own marker — so
 * `parseQuickBooksCustomerConflict` returns null for it and the generic 23505
 * branch would flatten it to "A record with these details already exists.",
 * hiding WHICH customer the company already bills from and which of the two
 * fixes applies (unlink it, or link this one as a secondary customer).
 *
 * `linkCustomerToCompany` in the link store already returns this sentence for
 * callers that go through it. This pre-check covers the transports that do
 * not — the QuickBooks entity API route, and any pg_graphql company edit that
 * writes the mirror column — where the raw Postgres error reaches the mapper.
 */
const quickBooksCompanyCustomerPreCheck: UserFacingPreCheck = (error) => {
  const companyConflict = parseQuickBooksCompanyCustomerConflict(error);
  if (!companyConflict) return null;
  return {
    status: 409,
    code: "QUICKBOOKS_COMPANY_CUSTOMER_TAKEN",
    error: quickBooksCompanyCustomerConflictMessage(companyConflict),
  };
};

/** Hub's domain refusals, in the order they are tried. */
const HUB_PRE_CHECKS: readonly UserFacingPreCheck[] = [
  contactMethodPreCheck,
  quickBooksCustomerPreCheck,
  quickBooksCompanyCustomerPreCheck,
];

export function mapToUserFacingError(
  error: unknown,
  context: MapToUserFacingErrorContext,
): UserFacingErrorResult {
  return mapToUserFacingErrorShared(error, {
    ...context,
    // Hub's refusals stay first; a caller may still append its own.
    preChecks: context.preChecks
      ? [...HUB_PRE_CHECKS, ...context.preChecks]
      : HUB_PRE_CHECKS,
  });
}

/** Enrollment-specific Postgres code mapping (duplicate live enrollment, etc.). */
export function mapSequenceEnrollError(error: unknown): UserFacingErrorResult {
  return mapToUserFacingError(error, {
    operation: "enrollment",
    overrides: {
      "23505": {
        status: 409,
        code: "ALREADY_ENROLLED",
        error:
          "This contact is already on that cadence. Choose a different sequence, or pause the existing enrollment from the contact's Sequence tab.",
      },
    },
  });
}
